/* eslint-disable max-len */
import { EtTransaction, LandToken } from "../types/LandToken";
import { generatePath, Link as _Link } from "react-router-dom";
import { AppRoutes, landDetailLink } from "../config";
import {
  Skeleton,
  styled,
  Tooltip,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Grid,
  Divider,
  Popover,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Close } from "@mui/icons-material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import moment from "moment/moment";
import { auctionCurrentPrice, sizing, toReadable, sixDigits } from "./methods";
import CloseIcon from "@mui/icons-material/Close";
import {
  CURRENCY_NAME,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  EcoTokenStatus,
  EtTransactionType,
  onVerification,
  TERM_UNIT,
  TIME_DISPLAY_UNIT,
  TREE_CURRENCY,
} from "./constants";
import { getEventName } from "../pages/EcoBank/LatestTransaction";
import { MapLink } from "../pages/Home/Home";
import { formatValue } from "./formatValue";
import { landImageSrc } from "./landImage";
import {
  TableSchema,
  AddressLink,
  Text,
  PulseCircle,
  CountdownTimer,
  CurrencyLabel,
  TruncatedText,
} from "../components";
import { DetailsPopup } from "../components/TransactionDetails/DetailsPopup";
import DigitFlip from "../components/DigitFlip";
import LandPriceCell from "../components/LandPriceCell";
import { PopUpText } from "../pages/LandPage";
import { resolveStandardStatus } from "./standard-status";
import HoloProgressBar from "../components/HoloProgressBar";

export const formatRelativeTime = (date: Date | string | number | undefined) => {
  if (!date) return "-";
  let str = moment(date).fromNow();
  if (str === "a few seconds ago") return "just now";

  return str
    .replace("an hour", "1 hour")
    .replace("a minute", "1 minute")
    .replace("a day", "1 day")
    .replace("a month", "1 month")
    .replace("a year", "1 year")
    .replace(" seconds", "s")
    .replace(" second", "s")
    .replace(" minutes", "m")
    .replace(" minute", "m")
    .replace(" hours", "h")
    .replace(" hour", "h")
    .replace(" days", "d")
    .replace(" day", "d")
    .replace(" months", "mo")
    .replace(" month", "mo")
    .replace(" years", "y")
    .replace(" year", "y");
};

export const Link = styled(_Link)`
  display: block;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  &:visited {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  &:hover {
    color: ${({ theme }) => theme.palette.action.disabled};
  }
`;

const Badge = styled("div")`
  text-transform: uppercase;
  width: max-content;
  font-family: IBM Mono, monospace;
  font-weight: 500;
`;

export const Img = styled("img")(() => ({
  display: "block",
  border: "none",
  // borderColor: theme.palette.border.dark,

  margin: 0,
  padding: 0,
}));

export const imgCol: TableSchema<LandToken> = {
  id: "content",
  label: "Land",
  sx: {
    width: { xs: "68px", md: "124px" },
    minWidth: { xs: "68px", md: "124px" },
    maxWidth: { xs: "68px", md: "124px" },
  },
  headerStyle: {
    width: { xs: "68px", md: "124px" },
    maxWidth: { xs: "68px", md: "124px" },
    minWidth: { xs: "68px", md: "124px" },
  },
  skeleton: () => {
    return <Skeleton sx={{ width: sizing(50), height: sizing(50) }} />;
  },
  render: (data) => {
    const isPublic = data.status == EcoTokenStatus.DRAFT && !data.trust;

    return (
      <Link
        sx={{ pointerEvents: isPublic ? "none" : "auto", width: sizing(50), height: sizing(50), margin: "0" }}
        to={landDetailLink(data.id || "")}
      >
        <Img
          alt="Thumb"
          sx={{
            width: "100%",
            backgroundColor: "transparent",
            objectFit: "cover",
            height: "100%",
            borderRadius: "20px",
          }}
          // The small static thumb (~20KB), not "full": that's a 30MB+ animated file per row,
          // which held up the whole page on a cold load. Lazy so off-screen rows wait.
          src={landImageSrc(data, "thumb") || "/favicon.ico"}
          loading="lazy"
          decoding="async"
        />
      </Link>
    );
  },
};

interface CustomColType<T> {
  (col: TableSchema<T>, data: T): TableSchema<T>;
}
export const customCol: CustomColType<LandToken> = (col, data) => ({
  ...col,
  render: () => {
    if (col.render) {
      return col.render(data);
    }
  },
});

export const customNameCol = (token: LandToken) => {
  return {
    ...nameCol,
    render: () => {
      if (nameCol.render) {
        return nameCol.render(token);
      }
    },
  };
};

// The Name column is pinned to the same width in every table (header + body cells)…
const NAME_COL_WIDTH = { xs: "150px", md: "265px" };
// …and the land name itself is clamped to that minus the cell's right padding (18px / 24px), so
// a long place name ellipsises on one line instead of wrapping onto a second row. The clamp has
// to be a px value on the text: these tables lay out `auto`, where a max-width on the cell is
// ignored and the column simply grows to fit the longest name.
const NAME_TEXT_WIDTH = { xs: "132px", md: "241px" };

export const nameCol: TableSchema<LandToken> = {
  id: "name",
  label: "Name",
  sx: {
    width: NAME_COL_WIDTH,
    minWidth: NAME_COL_WIDTH,
    maxWidth: NAME_COL_WIDTH,
  },
  headerStyle: {
    width: NAME_COL_WIDTH,
    maxWidth: NAME_COL_WIDTH,
    minWidth: NAME_COL_WIDTH,
  },
  skeleton: () => {
    return <Skeleton />;
  },
  render: (data: LandToken) => {
    const canClick = data.status === EcoTokenStatus.DRAFT && !data.trust;
    return (
      <Link sx={{ pointerEvents: canClick ? "none" : "auto" }} to={landDetailLink(data.id || "")}>
        <ClickableContainer sx={{ flexDirection: "column", alignItems: "flex-start", maxWidth: "100%" }}>
          <TruncatedText title={data.meta?.name || ""} sx={{ maxWidth: NAME_TEXT_WIDTH }}>
            <Text
              numeric
              capital
              contrast
              sx={{
                fontSize: { xs: 14, md: 16 },
                textDecoration: "none",
                lineHeight: 1.2,
                color: "black",
                "a:hover &": { color: "black" },
              }}
            >
              {data.meta?.name}
            </Text>
          </TruncatedText>
          {(data.etNft?.id || data.etNft?.id === 0) && (
            <Text
              numeric
              sx={{
                color: "text.disabled",
                lineHeight: 1.2,
              }}
            >
              #{data.etNft.id}
            </Text>
          )}
        </ClickableContainer>
      </Link>
    );
  },
};
export const locationCol: TableSchema<LandToken> = {
  id: "location",
  label: "Location",
  sx: {
    width: { xs: "110px", md: "150px" },
    minWidth: { xs: "110px", md: "150px" },
    maxWidth: { xs: "110px", md: "150px" },
  },
  headerStyle: {
    width: { xs: "110px", md: "150px" },
    maxWidth: { xs: "110px", md: "150px" },
    minWidth: { xs: "110px", md: "150px" },
  },
  render: (data) => {
    if (typeof data.meta?.location === "string") {
      const loc = JSON.parse(data.meta.location as any);
      return (
        <MapLink
          sx={{ color: "primary.main", textTransform: "uppercase", textWrap: "balance", textDecoration: "none" }}
          to={AppRoutes.Explore.path + "?id=" + data.id}
        >
          <ClickableContainer sx={{ flexDirection: "column" }}>
            <span style={{ fontWeight: 500, textWrap: "balance", wordBreak: "break-word" }}>{loc?.name?.trim()}</span>

            <Text sx={{ color: "text.disabled", whiteSpace: "nowrap" }}>{loc.country}</Text>
          </ClickableContainer>
        </MapLink>
      );
    } else {
      return (
        <MapLink sx={{ textWrap: "balance" }} to={AppRoutes.Explore.path + "?id=" + data.id}>
          {data.meta?.location?.name?.trim() + ", " + data.meta?.location.country}
        </MapLink>
      );
    }
  },
};
export const createdCol: TableSchema<LandToken> = {
  id: "created",
  label: "Age",
  render: (data: LandToken, _index) => (
    <Tooltip arrow={true} placement="bottom" title={moment(data.etNft?.createdAt).format(DATE_TIME_FORMAT)}>
      <span>{formatRelativeTime(data.etNft?.createdAt)}</span>
    </Tooltip>
  ),
};
export const guardianCol: TableSchema<LandToken> = {
  id: "address",
  label: "Guardian",
  render: (data: LandToken, _index) => (
    <ClickableContainer>
      <AddressLink
        sx={{
          "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
          "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
        }}
        type="user"
        address={data.minterAddress}
      />
    </ClickableContainer>
  ),
};
export const ownerCol: TableSchema<LandToken> = {
  id: "address",
  label: "Patron",
  render: ({ etNft }: LandToken) => {
    if (etNft?.ownerAddress === etNft?.minterAddress) {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    } else {
      return <AddressLink sx={{ textDecoration: "underline" }} type="user" address={etNft?.ownerAddress || ""} />;
    }
  },
};
const TrustColWrap = styled("div")`
  a span {
    max-width: 115px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    text-wrap: nowrap;
  }
`;
export const trustCol: TableSchema<LandToken> = {
  id: "trust",
  label: "Trustee",
  sx: { width: "auto", minWidth: 100, maxWidth: "max-content", boxSizing: "border-box" },
  render: (data, _index) => (
    <TrustColWrap>
      <ClickableContainer>
        <AddressLink
          sx={{
            "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
            "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
          }}
          type="trust"
          address={data.trust?.address as string}
          // name={data.trust?.organization?.name}
        />
      </ClickableContainer>
    </TrustColWrap>
  ),
};
export const statusCol: TableSchema<LandToken> = {
  id: "eftClaimed",
  label: "Status",
  render: ({ etNft }: LandToken) => {
    return (
      <Text numeric contrast bold medium capital>
        {etNft?.easementActivated?.active ? "Active" : "-"}
      </Text>
    );
  },
};

export const guardianStatusCol: TableSchema<LandToken> = {
  id: "guardianStatus",
  label: "Status",
  render: ({ status, etNft, meta }: LandToken) => {
    const standardized = resolveStandardStatus({ status, etNft, meta }, { role: "guardian" });
    if (standardized) {
      return (
        <Text numeric capital bold contrast sx={{ color: standardized.color }}>
          {standardized.showPulse && (
            <PulseCircle
              warning={!!standardized.pulseWarning}
              sold={!!standardized.pulseSold && !standardized.pulseWarning}
            />
          )}
          {standardized.label}
        </Text>
      );
    }

    console.log({ status, etNft, meta });
    let text = "Verification";
    let color = "text.primary";
    let pulseSuccess = false;
    let pulseWarning = false;
    let showPulse = false;

    const isVerification = onVerification(status) || status === EcoTokenStatus.VERIFICATION_REQUIRED;
    const hasActivatedEasement = etNft?.easementActivated && etNft.easementActivated.active;

    // Determine status based on priority
    switch (true) {
      case isVerification:
        showPulse = true;
        pulseWarning = true;
        text = "Verification";
        color = "text.primary";
        break;

      case status === EcoTokenStatus.APPROVED:
        text = "Verified";
        color = "text.primary";
        break;

      case status === EcoTokenStatus.VERIFICATION_FAILED:
        text = "Rejected";
        color = "warning.main";
        break;

      case etNft?.id !== undefined && etNft?.status === EcoTokenStatus.VERIFIED:
        text = "Verified";
        color = "text.primary";
        break;

      case etNft?.id !== undefined && etNft?.status === "auction":
        showPulse = true;
        pulseWarning = false;
        text = "Live auction";
        color = "action.pulse";
        break;
      case hasActivatedEasement: {
        const easementActivated = etNft?.easementActivated;
        if (easementActivated) {
          const start = moment(easementActivated.contractStart);
          const duration = meta?.contractPeriod || 3;
          // TODO change to years
          if (start.add(duration, TIME_DISPLAY_UNIT).isAfter(moment())) {
            pulseSuccess = true;
            showPulse = true;
            text = "Active";
            color = "action.pulse";
          } else {
            showPulse = false;
            const total = formatValue(easementActivated.treeTotal, false);
            const claimed = formatValue(easementActivated.rewardClaimed, false);
            if (total && total - claimed > 0) {
              text = "Active";
              showPulse = true;
              pulseSuccess = true;
              color = "action.pulse";
            } else {
              text = "Expired";
              showPulse = false;
              color = "text.primary";
            }
          }
        }
        break;
      }
    }

    return (
      <Text numeric capital contrast sx={{ color: color }}>
        {showPulse && <PulseCircle warning={pulseWarning} sold={pulseSuccess && !pulseWarning} />}
        {text}
      </Text>
    );
  },
};

export const publicStatusCol: TableSchema<LandToken> = {
  id: "publicStatusCol",
  label: "Status",
  render: ({ status, etNft, meta, offerStatus }: LandToken) => {
    const standardized = resolveStandardStatus({ status, etNft, meta, offerStatus }, { role: "public" });
    if (standardized) {
      const isOfferOrAuction = standardized.label === "Offer" || standardized.label === "Auction";
      return (
        <Text numeric bold capital contrast sx={{ color: standardized.color }}>
          {isOfferOrAuction ? (
            <ListAltOutlinedIcon sx={{ fontSize: 14, mr: "4px" }} />
          ) : (
            standardized.showPulse && (
              <PulseCircle
                warning={!!standardized.pulseWarning}
                sold={!!standardized.pulseSold && !standardized.pulseWarning}
              />
            )
          )}
          {standardized.label}
        </Text>
      );
    }

    let text = "Verification";
    let color = "text.primary";
    let pulseSuccess = false;
    let pulseWarning = false;
    let showPulse = false;
    console.log({ status, etNft, meta, offerStatus });

    const isVerification = onVerification(status) || status === EcoTokenStatus.VERIFICATION_REQUIRED;
    const isLiveOffer = offerStatus === "opened";
    const hasActivatedEasement = etNft?.easementActivated && etNft.easementActivated.active;

    switch (true) {
      case isLiveOffer:
        showPulse = true;
        pulseWarning = true;
        color = "action.pulse";
        text = "Live Offer";
        break;

      case isVerification:
        showPulse = true;
        pulseWarning = true;
        text = "Verification";
        break;

      case status === EcoTokenStatus.APPROVED:
        text = "Verified";
        color = "#565656";
        break;

      case status === EcoTokenStatus.VERIFICATION_FAILED:
        text = "Rejected";
        color = "warning.main";
        break;

      case etNft?.id !== undefined && etNft?.status === EcoTokenStatus.VERIFIED:
        text = "Verified";
        color = "#565656";
        break;

      case etNft?.id !== undefined && etNft?.status === "auction":
        showPulse = true;
        pulseWarning = false;
        text = "Live auction";
        color = "action.pulse";
        break;

      case hasActivatedEasement: {
        const easementActivated = etNft?.easementActivated;
        if (easementActivated) {
          const start = moment(easementActivated.contractStart);
          const duration = meta?.contractPeriod || 3;
          // TODO change to years
          if (start.add(duration, TIME_DISPLAY_UNIT).isAfter(moment())) {
            pulseSuccess = true;
            showPulse = true;
            text = "Active";
            color = "action.pulse";
          } else {
            showPulse = false;
            const total = formatValue(easementActivated.treeTotal, false);
            const claimed = formatValue(easementActivated.rewardClaimed, false);
            if (total && total - claimed > 0) {
              text = "Active";
              showPulse = true;
              pulseSuccess = true;
              color = "action.pulse";
            } else {
              text = "Expired";
            }
          }
        }
        break;
      }

      case status === EcoTokenStatus.VERIFIED:
        text = "Verified";
        color = "#565656";
        break;
    }

    return (
      <Text numeric capital contrast sx={{ color: color }}>
        {showPulse && <PulseCircle warning={pulseWarning} sold={pulseSuccess && !pulseWarning} />}
        {text}
      </Text>
    );
  },
};

export const auctionStatusCol: TableSchema<LandToken> = {
  id: "auction",
  label: "Status",
  sx: { width: 150, minWidth: 150, maxWidth: 150, boxSizing: "border-box" },
  render: ({ status, etNft, meta, offerStatus }: LandToken) => {
    const standardized = resolveStandardStatus({ status, etNft, meta, offerStatus }, { role: "public" });
    if (standardized) {
      const isOfferOrAuction = standardized.label === "Offer" || standardized.label === "Auction";
      return (
        <Text medium contrast bold numeric capital sx={{ color: standardized.color, alignItems: "center" }}>
          {isOfferOrAuction ? (
            <ListAltOutlinedIcon sx={{ fontSize: 14, mr: "4px" }} />
          ) : (
            standardized.showPulse && (
              <PulseCircle
                warning={!!standardized.pulseWarning}
                sold={!!standardized.pulseSold && !standardized.pulseWarning}
              />
            )
          )}
          {standardized.label}
        </Text>
      );
    }

    const hasNft = !!etNft;
    const hasAuction = !!etNft?.etAuction;
    const isActive = etNft?.easementActivated?.active;
    const isExpired =
      isActive &&
      moment(etNft?.easementActivated?.contractStart).add(meta?.contractPeriod, TERM_UNIT).isBefore(moment());
    const remainingTrees =
      formatValue(etNft?.easementActivated?.treeTotal as any, false) -
      (formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0);
    const isTreesDepleted = isActive && isExpired && remainingTrees <= 0;

    const isSold = etNft?.minterAddress !== etNft?.ownerAddress;
    const isEnded =
      etNft?.etAuction?.status === "ended" ||
      etNft?.etAuction?.status === "completed" ||
      etNft?.etAuction?.status === "cancelled";
    const isLiveOffer = offerStatus === "opened";

    if (isLiveOffer) {
      return (
        <Text
          medium
          contrast
          numeric
          capital
          sx={{
            color: "action.pulse",
            alignItems: "center",
          }}
        >
          <PulseCircle sold={isSold} />
          Live Offer
        </Text>
      );
    }
    if (!hasNft) {
      return (
        <Text medium contrast numeric capital>
          {status.replace("_", " ")}
        </Text>
      );
    }

    if (hasNft && !hasAuction) {
      return (
        <Text medium contrast numeric capital>
          {etNft.status === "minted" ? "Verified" : etNft.status.replace("_", " ")}
        </Text>
      );
    }

    if (hasNft && hasAuction) {
      return (
        <Tooltip
          arrow={true}
          placement="bottom"
          title={moment(etNft?.etAuction?.auctionStart).format(DATE_TIME_FORMAT)}
        >
          <>
            {isSold ? (
              <span>
                <Text
                  medium
                  contrast
                  numeric
                  capital
                  sx={{
                    color: isTreesDepleted ? "text.primary" : "action.pulse",
                    alignItems: "center",
                  }}
                >
                  <PulseCircle sold={isSold} hide={isTreesDepleted} />
                  {isTreesDepleted ? "Expired" : isActive ? "Active" : "Live auction"}
                </Text>
              </span>
            ) : (
              <span>
                <Text
                  medium
                  numeric
                  contrast
                  capital
                  sx={{
                    color:
                      isEnded && !isSold
                        ? "text.primary"
                        : isEnded && isActive
                        ? "text.primary"
                        : isSold && isEnded
                        ? "text.primary"
                        : "action.pulse",
                    alignItems: "center",
                  }}
                >
                  {!isEnded && !isSold && <PulseCircle warning={isEnded && !isSold} />}
                  {isEnded && !isSold ? "Verified" : "Live auction"}
                </Text>
                {!isEnded && !isSold && (
                  <Text numeric>
                    <CountdownTimer
                      medium
                      sx={{ width: "100%", color: "#565656" }}
                      targetDate={
                        moment(etNft?.etAuction?.auctionStart).add(etNft?.etAuction?.duration, "ms").toDate() as Date
                      }
                    />
                  </Text>
                )}
              </span>
            )}
          </>
        </Tooltip>
      );
    }

    return (
      <Text numeric contrast medium>
        {status}
      </Text>
    );
  },
};

export const treeMintPoolCol: TableSchema<LandToken> = {
  id: "eftMintPool",
  label: "Mint capacity",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  render: ({ etNft }: LandToken) => {
    const weiValue = etNft?.easementActivated?.treeTotal || etNft?.totalTreeForEasement;
    const val = weiValue ? toReadable(weiValue, true, true, 3) : undefined;
    if (val) {
      return (
        <Text numeric contrast medium>
          {sixDigits(val)} <CurrencyLabel />
        </Text>
      );
    } else {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    }
  },
};

export const treeMintRateCol: TableSchema<LandToken> = {
  id: "eftMintRate",
  label: "Mint per day",
  sx: { width: 140, minWidth: 140, maxWidth: 140, boxSizing: "border-box" },
  render: ({ meta, etNft }: LandToken) => {
    // Create responsive CountdownTimer component
    const ResponsiveCountdownTimer = () => {
      return (
        <CountdownTimer
          // small={!isMobile}
          // medium={isMobile}
          medium
          sx={{ marginLeft: 4 }}
          targetDate={moment(etNft?.easementActivated?.contractStart)
            .add(meta?.contractPeriod, TIME_DISPLAY_UNIT)
            .toDate()}
        />
      );
    };

    const treeSupplyData = {
      mintCapacity: etNft?.totalTreeForEasement ? formatValue(etNft.totalTreeForEasement, true) : "0 TREEs",
      guardianRedeemedReward: etNft?.easementActivated?.guardianRedeemedReward
        ? formatValue(etNft.easementActivated.guardianRedeemedReward, true)
        : "0 TREEs",
      patronRedeemedReward: etNft?.easementActivated?.patronRedeemedReward
        ? formatValue(etNft.easementActivated.patronRedeemedReward, true)
        : "0 TREEs",
      referralRedeemedReward: etNft?.easementActivated?.referralRedeemedReward
        ? formatValue(etNft.easementActivated.referralRedeemedReward, true)
        : "0 TREEs",
      claimedMint: etNft?.easementActivated?.rewardClaimed
        ? formatValue(etNft.easementActivated.rewardClaimed, true)
        : "0 TREEs",
      remainingMint: (() => {
        const total = formatValue(etNft?.totalTreeForEasement as any, false);
        const claimed = formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0;
        const remaining = total - claimed;
        const remainingDecimalPlaces =
          remaining <= 0 ? 0 : remaining > 999999 ? 0 : Math.max(0, 6 - Math.floor(Math.log10(remaining) + 1));
        return formatValue(remaining, true, remainingDecimalPlaces);
      })(),
      activeTime: etNft?.easementActivated?.contractStart ? (
        <ResponsiveCountdownTimer />
      ) : (
        `${meta?.contractPeriod || 0} ${TERM_UNIT.toUpperCase()}`
      ),
    };

    const weiValue = etNft?.easementActivated?.treeTotal || etNft?.totalTreeForEasement;
    const days = moment.duration(meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    const rate = weiValue ? (toReadable(weiValue, true, false) as number) / (days - 1) : undefined;

    if (rate) {
      return <TreeSupplyDetails rate={rate} treeSupplyData={treeSupplyData} />;
    } else {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    }
  },
};

export const remainingMintCol: TableSchema<LandToken> = {
  id: "treeValue",
  label: "Value",
  sx: { width: 140, minWidth: 140, maxWidth: 140, boxSizing: "border-box" },
  render: ({ meta, etNft }: LandToken) => {
    // Create responsive CountdownTimer component
    const ResponsiveCountdownTimer = () => {
      return (
        <CountdownTimer
          // small={!isMobile}
          // medium={isMobile}
          medium
          sx={{ marginLeft: 4 }}
          targetDate={moment(etNft?.easementActivated?.contractStart)
            .add(meta?.contractPeriod, TIME_DISPLAY_UNIT)
            .toDate()}
        />
      );
    };

    const total = formatValue(etNft?.totalTreeForEasement as any, false);
    const claimed = formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    const remaining = total - claimed;
    const remainingDecimalPlaces =
      remaining <= 0 ? 0 : remaining > 999999 ? 0 : Math.max(0, 6 - Math.floor(Math.log10(remaining) + 1));

    const treeSupplyData = {
      mintCapacity: etNft?.totalTreeForEasement ? formatValue(etNft.totalTreeForEasement, true) : "0 TREEs",
      guardianRedeemedReward: etNft?.easementActivated?.guardianRedeemedReward
        ? formatValue(etNft.easementActivated.guardianRedeemedReward, true)
        : "0 TREEs",
      patronRedeemedReward: etNft?.easementActivated?.patronRedeemedReward
        ? formatValue(etNft.easementActivated.patronRedeemedReward, true)
        : "0 TREEs",
      referralRedeemedReward: etNft?.easementActivated?.referralRedeemedReward
        ? formatValue(etNft.easementActivated.referralRedeemedReward, true)
        : "0 TREEs",
      claimedMint: etNft?.easementActivated?.rewardClaimed
        ? formatValue(etNft.easementActivated.rewardClaimed, true)
        : "0 TREEs",
      remainingMint: formatValue(remaining, true, remainingDecimalPlaces),
      activeTime: etNft?.easementActivated?.contractStart ? (
        <ResponsiveCountdownTimer />
      ) : (
        `${meta?.contractPeriod || 0} ${TERM_UNIT.toUpperCase()}`
      ),
    };

    const weiValue = etNft?.easementActivated?.treeTotal || etNft?.totalTreeForEasement;
    const days = moment.duration(meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    const rate = weiValue ? (toReadable(weiValue, true, false) as number) / (days - 1) : undefined;

    if (rate) {
      return <TreeSupplyDetails rate={remaining} treeSupplyData={treeSupplyData} />;
    } else {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    }
  },
};

export const remainingMintBarCol: TableSchema<LandToken> = {
  id: "treeValue",
  label: "Mintable",
  sx: {
    width: { xs: "175px", md: "200px" },
    minWidth: { xs: "175px", md: "200px" },
    maxWidth: { xs: "175px", md: "200px" },
  },
  headerStyle: {
    width: { xs: "175px", md: "200px" },
    maxWidth: { xs: "175px", md: "200px" },
    minWidth: { xs: "175px", md: "200px" },
  },
  render: ({ meta, etNft }: LandToken) => {
    // Create responsive CountdownTimer component
    // const ResponsiveCountdownTimer = () => {
    //   return (
    //     <CountdownTimer
    //       // small={!isMobile}
    //       // medium={isMobile}
    //       medium
    //       sx={{ marginLeft: 4 }}
    //       targetDate={moment(etNft?.easementActivated?.contractStart)
    //         .add(meta?.contractPeriod, TIME_DISPLAY_UNIT)
    //         .toDate()}
    //     />
    //   );
    // };

    const total = formatValue(etNft?.totalTreeForEasement as any, false);
    const claimed = formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    const remaining = total - claimed;
    const progress = total > 0 ? (remaining / total) * 100 : 0;
    const remainingPercentage = total > 0 ? (remaining / total) * 100 : 0;

    // const treeSupplyData = {
    //   mintCapacity: etNft?.totalTreeForEasement ? formatValue(etNft.totalTreeForEasement, true) : "0 TREEs",
    //   guardianRedeemedReward: etNft?.easementActivated?.guardianRedeemedReward
    //     ? formatValue(etNft.easementActivated.guardianRedeemedReward, true)
    //     : "0 TR3",
    //   patronRedeemedReward: etNft?.easementActivated?.patronRedeemedReward
    //     ? formatValue(etNft.easementActivated.patronRedeemedReward, true)
    //     : "0 TR3",
    //   referralRedeemedReward: etNft?.easementActivated?.referralRedeemedReward
    //     ? formatValue(etNft.easementActivated.referralRedeemedReward, true)
    //     : "0 TR3",
    //   claimedMint: etNft?.easementActivated?.rewardClaimed
    //     ? formatValue(etNft.easementActivated.rewardClaimed, true)
    //     : "0 TR3",
    //   remainingMint: formatValue(remaining, true, remainingDecimalPlaces),
    //   activeTime: etNft?.easementActivated?.contractStart ? (
    //     <ResponsiveCountdownTimer />
    //   ) : (
    //     `${meta?.contractPeriod || 0} ${TERM_UNIT.toUpperCase()}`
    //   ),
    // };

    const weiValue = etNft?.easementActivated?.treeTotal || etNft?.totalTreeForEasement;
    const days = moment.duration(meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    const rate = weiValue ? (toReadable(weiValue, true, false) as number) / (days - 1) : undefined;

    if (rate) {
      return (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              // Sits the 10px bar where locationCol's second line (the country) reads: that line
              // is 4px below the first with its caps centred in a 19.2px line box, so the bar
              // needs the 4px plus ~half the leftover leading to land on the same gap.
              mb: "8px",
            }}
          >
            {/* Both figures flip (transitions.dev number pop-in) when a claim lowers them for
                this token. The inner span keeps the space before TR3: Text is a flex box, and a
                bare text item after DigitFlip would have its leading space collapsed. */}
            <Text numeric contrast medium>
              <span>
                <DigitFlip
                  value={String(sixDigits(formatValue(remaining, true, 2)))}
                  trackKey={etNft?.id}
                  scope="mintable"
                />{" "}
                TR3
              </span>
            </Text>

            <Text small sx={{ color: "text.disabled", alignItems: "center" }}>
              <DigitFlip value={`(${remainingPercentage.toFixed(0)}%)`} trackKey={etNft?.id} scope="mintable-pct" />
            </Text>
          </Box>
          <Box>
            {/* Same bar as the LandPage Mintable field; fills in as the row scrolls into view. */}
            <HoloProgressBar value={progress} aria-label="Mintable TR3 remaining" fillIn />
          </Box>
        </>
      );
    } else {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    }
  },
};

export const totalMintCol: TableSchema<LandToken> = {
  id: "treeValue",
  label: "Mint Cap",
  sx: { width: 140, minWidth: 140, maxWidth: 140, boxSizing: "border-box" },
  render: ({ meta, etNft }: LandToken) => {
    // Create responsive CountdownTimer component
    const ResponsiveCountdownTimer = () => {
      return (
        <CountdownTimer
          // small={!isMobile}
          // medium={isMobile}
          medium
          sx={{ marginLeft: 4 }}
          targetDate={moment(etNft?.easementActivated?.contractStart)
            .add(meta?.contractPeriod, TIME_DISPLAY_UNIT)
            .toDate()}
        />
      );
    };

    const total = formatValue(etNft?.totalTreeForEasement as any, false);
    const claimed = formatValue(etNft?.easementActivated?.rewardClaimed as any, false) || 0;
    const remaining = total - claimed;
    const remainingDecimalPlaces =
      remaining <= 0 ? 0 : remaining > 999999 ? 0 : Math.max(0, 6 - Math.floor(Math.log10(remaining) + 1));

    const treeSupplyData = {
      mintCapacity: etNft?.totalTreeForEasement ? formatValue(etNft.totalTreeForEasement, true) : "0 TREEs",
      guardianRedeemedReward: etNft?.easementActivated?.guardianRedeemedReward
        ? formatValue(etNft.easementActivated.guardianRedeemedReward, true)
        : "0 TREEs",
      patronRedeemedReward: etNft?.easementActivated?.patronRedeemedReward
        ? formatValue(etNft.easementActivated.patronRedeemedReward, true)
        : "0 TREEs",
      referralRedeemedReward: etNft?.easementActivated?.referralRedeemedReward
        ? formatValue(etNft.easementActivated.referralRedeemedReward, true)
        : "0 TREEs",
      claimedMint: etNft?.easementActivated?.rewardClaimed
        ? formatValue(etNft.easementActivated.rewardClaimed, true)
        : "0 TREEs",
      remainingMint: formatValue(remaining, true, remainingDecimalPlaces),
      activeTime: etNft?.easementActivated?.contractStart ? (
        <ResponsiveCountdownTimer />
      ) : (
        `${meta?.contractPeriod || 0} ${TERM_UNIT.toUpperCase()}`
      ),
    };

    const weiValue = etNft?.easementActivated?.treeTotal || etNft?.totalTreeForEasement;
    const days = moment.duration(meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    const rate = weiValue ? (toReadable(weiValue, true, false) as number) / (days - 1) : undefined;

    if (rate) {
      return <TreeSupplyDetails rate={total} treeSupplyData={treeSupplyData} />;
    } else {
      return (
        <Text numeric contrast medium>
          -
        </Text>
      );
    }
  },
};

export const valueCol: TableSchema<LandToken> = {
  id: "value",
  label: "Price",
  sx: { width: 120, minWidth: 120, maxWidth: 120, boxSizing: "border-box" },
  render: ({ etNft }: LandToken) => {
    return (
      <Text numeric contrast medium>
        {toReadable(auctionCurrentPrice(etNft?.etAuction) || "0", true, true, 2)}
        <CurrencyLabel currency={CURRENCY_NAME} />
      </Text>
    );
  },
};

// The land's live price (its current bid or top offer) over the price it last sold for, laid out
// like locationCol. LandPriceCell reads both off the row where it can and fetches the rest.
export const txnLatestLandCol: TableSchema<LandToken> = {
  id: "price",
  label: "Price",
  // Room for a two-decimal six-figure price ("125,000.25 USD") without running into the next column.
  sx: { width: 150, minWidth: 150, maxWidth: 150, boxSizing: "border-box" },
  render: (land: LandToken) => <LandPriceCell land={land} />,
};

export const scoreCol: TableSchema<LandToken> = {
  id: "value",
  label: "Score",
  sx: {
    width: { xs: "100px", md: "100px" },
    minWidth: { xs: "100px", md: "100px" },
    maxWidth: { xs: "100px", md: "100px" },
  },
  headerStyle: {
    width: { xs: "100px", md: "100px" },
    maxWidth: { xs: "100px", md: "100px" },
    minWidth: { xs: "100px", md: "100px" },
  },
  render: ({ ecoScore }: LandToken) => {
    const isNan = isNaN(ecoScore * 100);
    return (
      <Text numeric contrast medium>
        {isNan || !ecoScore ? "-" : (ecoScore * 100).toFixed(0)}
      </Text>
    );
  },
};

export const ageCol: TableSchema<LandToken> = {
  id: "lastTxn",
  label: "Last Txn",
  sx: {
    width: { xs: "115px", md: "115px" },
    minWidth: { xs: "115px", md: "115px" },
    maxWidth: { xs: "115px", md: "115px" },
  },
  headerStyle: {
    width: { xs: "115px", md: "115px" },
    maxWidth: { xs: "115px", md: "115px" },
    minWidth: { xs: "115px", md: "115px" },
  },
  render: (data: LandToken) => {
    return (
      <Link to={landDetailLink(data.id || "") + "#provenance"}>
        <ClickableContainer>
          <Text medium sx={{ color: "text.primary", fontFamily: "monospace", textTransform: "lowercase" }}>
            {formatRelativeTime(data.lastTxn)}
            <ArrowOutwardIcon
              sx={{
                color: "black",
                fontSize: "0.9rem",
              }}
            />
          </Text>
        </ClickableContainer>
      </Link>
    );
  },
};

export const ageColLatestLands: TableSchema<LandToken> = {
  id: "time",
  label: "Time",
  sx: {
    width: { xs: "115px", md: "115px" },
    minWidth: { xs: "115px", md: "115px" },
    maxWidth: { xs: "115px", md: "115px" },
  },
  headerStyle: {
    width: { xs: "115px", md: "115px" },
    maxWidth: { xs: "115px", md: "115px" },
    minWidth: { xs: "115px", md: "115px" },
  },
  render: (data: LandToken) => {
    return (
      <Link to={landDetailLink(data.id || "") + "#provenance"}>
        <ClickableContainer>
          <Text medium sx={{ color: "text.primary", fontFamily: "monospace", textTransform: "lowercase" }}>
            {formatRelativeTime(data.lastTxn)}
            <ArrowOutwardIcon
              sx={{
                color: "black",
                fontSize: "0.9rem",
              }}
            />
          </Text>
        </ClickableContainer>
      </Link>
    );
  },
};

export const auctionStartCol: TableSchema<LandToken> = {
  id: "auctionStart",
  label: "Auction Start",
  render: ({ etNft }: LandToken) => {
    return (
      <Text numeric medium>
        {moment(etNft?.etAuction.auctionStart).format(DATE_TIME_FORMAT)}
      </Text>
    );
  },
};

export const auctionEndCol: TableSchema<LandToken> = {
  id: "auctionEnd",
  label: "Auction End",
  render: ({ etNft }: LandToken) => {
    return (
      <Text numeric medium>
        {moment(etNft?.etAuction.auctionStart).add(etNft?.etAuction.duration, "ms").format(DATE_TIME_FORMAT)}
      </Text>
    );
  },
};

export const easementActivatedCol: TableSchema<LandToken> = {
  id: "easementActivated",
  label: "Activated",
  render: ({ etNft }: LandToken) => {
    return (
      <Text numeric medium>
        {moment(etNft?.easementActivated?.contractStart).format(DATE_FORMAT)}
      </Text>
    );
  },
};

export const baseLandsSchema: TableSchema<LandToken>[] = [imgCol, nameCol, locationCol];
export const ExploreLandsSchema: TableSchema<LandToken>[] = [
  imgCol,
  nameCol,
  locationCol,
  scoreCol,
  remainingMintBarCol,
  totalMintCol,
  auctionStatusCol,
  txnLatestLandCol,
  ageColLatestLands,
];

export const ExploreMobileSchema: TableSchema<LandToken>[] = [
  imgCol,
  nameCol,
  remainingMintCol,
  auctionStatusCol,
  // treeMintRateCol,
  valueCol,
];

export const transactionDetailsCol: TableSchema<EtTransaction> = {
  id: "details",
  label: "",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  headerStyle: { width: "25px" },
  render: (transaction) => {
    return <DetailsPopup simple transaction={transaction} />;
  },
};

export const hashCol: TableSchema<EtTransaction> = {
  id: "hash",
  label: "Hash",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  headerStyle: { width: "150px", minWidth: "120px" },
  render: (transaction) => {
    return (
      <div>
        <DetailsPopup transaction={transaction} />
        <Tooltip id={`txn${transaction.hash}`} title={moment(transaction.createdAt).format(DATE_TIME_FORMAT + ":ss")}>
          <div id={`txn${transaction.hash}`}>
            <Text numeric medium sx={{ color: "text.disabled" }}>
              {formatRelativeTime(transaction.createdAt)}
            </Text>
          </div>
        </Tooltip>
      </div>
    );
  },
};

export const timeCol: TableSchema<EtTransaction> = {
  id: "time",
  label: "Time",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  headerStyle: { width: "150px", minWidth: "120px" },
  render: (transaction) => {
    return <TimeCell transaction={transaction} />;
  },
};

const TimeCell = ({ transaction }: { transaction: EtTransaction }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const suppressRef = useRef(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleClick = () => {
    suppressRef.current = true;
    setTooltipOpen(false);
    wrapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
  };
  return (
    <ClickableContainer
      onClick={handleClick}
      onMouseLeave={() => {
        suppressRef.current = false;
      }}
    >
      <Tooltip
        id={`txn${transaction.hash}`}
        title={moment(transaction.createdAt).format(DATE_TIME_FORMAT + ":ss")}
        open={tooltipOpen}
        onOpen={() => {
          if (!suppressRef.current) setTooltipOpen(true);
        }}
        onClose={() => setTooltipOpen(false)}
      >
        <div id={`txn${transaction.hash}`} ref={wrapRef}>
          <Text medium sx={{ color: "text.primary", fontFamily: "monospace", textTransform: "lowercase" }}>
            {formatRelativeTime(transaction.createdAt)}
            <DetailsPopup
              transaction={transaction}
              time
              onClose={() => {
                suppressRef.current = false;
              }}
            />
          </Text>
        </div>
      </Tooltip>
    </ClickableContainer>
  );
};

export const transactionThumbCol: TableSchema<EtTransaction> = {
  id: "content",
  label: "Image",
  sx: {
    width: { xs: "68px", md: "124px" },
    minWidth: { xs: "68px", md: "124px" },
    maxWidth: { xs: "68px", md: "124px" },
  },
  headerStyle: {
    width: { xs: "68px", md: "124px" },
    maxWidth: { xs: "68px", md: "124px" },
    minWidth: { xs: "68px", md: "124px" },
  },
  skeleton: () => {
    return <Skeleton sx={{ width: sizing(50), height: sizing(50) }} />;
  },
  render: (txn: any) => {
    const isReassignLand = Boolean(txn?.type === "reassign_land");
    const userImage =
      txn?.type === EtTransactionType?.TRUST_DISMISSED ||
      txn?.type === EtTransactionType?.TRUST_FLAGGED_FOR_DISMISSAL ||
      txn?.type === EtTransactionType?.TRUST_ROLE_GRANTED ||
      txn?.type === EtTransactionType?.GLOBAL_ADMIN_ADDED ||
      txn?.type === EtTransactionType?.TRUST_ADMIN_ADDED;

    switch (true) {
      case isReassignLand:
        return (
          <Link
            sx={{
              width: sizing(50),
              height: sizing(50),
              margin: "0",
              cursor: txn?.nftId ? "pointer" : "not-allowed",
              pointerEvents: txn?.nftId ? "auto" : "none",
            }}
            to={landDetailLink(txn?.etNft?.ecoToken?.id || "")}
          >
            <Img
              alt="Thumb"
              sx={{ width: "100%", objectFit: "cover", height: "100%", borderRadius: "20px" }}
              src={txn?.data?.landImage}
            />
          </Link>
        );

      case userImage:
        return (
          <Link
            sx={{ width: sizing(50), height: sizing(50), margin: "0" }}
            to={generatePath(AppRoutes.UserWallet.path, { address: txn?.to || "" })}
          >
            <Img
              alt="Thumb"
              sx={{
                width: "100%",
                objectFit: txn.toUserDetails?.role === "admin" ? "cover" : "cover",
                scale: txn.toUserDetails?.role === "admin" ? 1 : 1,
                height: "100%",
                borderRadius: txn?.toUserDetails?.role === "admin" ? "50%" : "50%",
              }}
              src={txn.toUserDetails?.profilePicture}
            />
          </Link>
        );

      default:
        return (
          <Link
            sx={{ width: sizing(50), height: sizing(50), margin: "0" }}
            to={landDetailLink(txn?.etNft?.ecoToken?.id || "")}
          >
            <Img
              alt="Thumb"
              sx={{ width: "100%", objectFit: "cover", height: "100%", borderRadius: "20px" }}
              // Static thumb, not the raw `content` (an 80MB+ GIF for a 50px tile).
              src={landImageSrc(txn?.etNft?.ecoToken, "thumb")}
              loading="lazy"
              decoding="async"
            />
          </Link>
        );
    }
  },
};

export const transactionTypeCol: TableSchema<EtTransaction> = {
  id: "type",
  label: "Action",
  render: (transaction) => {
    return <Badge>{getEventName(transaction)}</Badge>;
  },
};

export const transactionValueCol: TableSchema<EtTransaction> = {
  id: "amount",
  label: "Value",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  render: (transaction) => {
    const isUpdated = transaction.type === "easement_updated";
    const isScanned = transaction.type === "easement_added";

    switch (transaction.type) {
      case EtTransactionType.CLAIM_FUNDS: {
        const amount = transaction.data.ecoFutureReward
          ? BigInt(transaction.data.ecoFutureReward || 0) +
            BigInt(transaction.data.guardianReward || 0) +
            BigInt(transaction.data.trustReward || 0)
          : BigInt(transaction.data.ecoFutureAmount || 0) +
            BigInt(transaction.data.guardianAmount || 0) +
            BigInt(transaction.data.trustAmount || 0);

        return (
          <Text numeric contrast medium>
            {toReadable(amount, true, true, 2)}
            <CurrencyLabel currency={CURRENCY_NAME} />
          </Text>
        );
      }

      default: {
        if (
          transaction.data?.value ||
          transaction.data?.price ||
          transaction.data?.amount ||
          transaction.data?.endingPrice ||
          transaction.data?.startingBid ||
          transaction?.data?.startingPrice ||
          isScanned
        ) {
          const value = toReadable(
            transaction.data.value * (isUpdated ? 100 : 1) ||
              transaction.data?.price ||
              transaction.data.amount ||
              transaction.data.endingPrice ||
              transaction.data.startingBid ||
              transaction?.data?.startingPrice ||
              transaction.etNft.ecoToken.ecoScore * (isScanned ? 100 : 1),
            false,
            true,
            2
          );
          return (
            <Text numeric contrast medium>
              {value}
              {!isUpdated && !isScanned ? (
                <CurrencyLabel currency={CURRENCY_NAME} />
              ) : (
                <CurrencyLabel currency="Score" />
              )}
            </Text>
          );
        }

        if (transaction.data?.guardianReward) {
          const amount = transaction.data.isPatron
            ? BigInt(transaction.data.patronReward)
            : BigInt(transaction.data.guardianReward) + BigInt(transaction.data.referralReward);

          return (
            <Text numeric contrast medium>
              {toReadable(amount, true, true, 2)} <CurrencyLabel />
            </Text>
          );
        }

        if (transaction.data?.txnData?.easement?.length && transaction.data.txnData.easement[3]) {
          return (
            <Text numeric contrast medium>
              {transaction.data.txnData.easement[3]} Score
            </Text>
          );
        }

        return (
          <Text numeric contrast medium>
            -
          </Text>
        );
      }
    }
  },
};

export const transactionFromCol: TableSchema<EtTransaction> = {
  id: "from",
  label: "From",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  render: (transaction) => {
    switch (transaction.type) {
      case EtTransactionType.UPDATED:
        return (
          <ClickableContainer>
            <AddressLink
              sx={{
                "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
              }}
              type="trust"
              address={transaction.data.trust}
            />
          </ClickableContainer>
        );

      default:
        if (transaction.from) {
          return (
            <ClickableContainer>
              <AddressLink
                sx={{
                  "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                  "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                }}
                type={transaction.type === EtTransactionType.MINTED ? "trust" : "user"}
                address={transaction.from}
              />
            </ClickableContainer>
          );
        }
        return (
          <ClickableContainer>
            <Text numeric contrast medium>
              -
            </Text>
          </ClickableContainer>
        );
    }
  },
};

export const transactionToCol: TableSchema<EtTransaction> = {
  id: "to",
  label: "To",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  render: (transaction) => {
    switch (transaction.type) {
      case EtTransactionType.UPDATED:
        return (
          <ClickableContainer>
            <AddressLink
              sx={{
                "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
              }}
              type="user"
              address={transaction.data.guardian}
            />
          </ClickableContainer>
        );

      case EtTransactionType.CLAIM_FUNDS:
        return (
          <ClickableContainer>
            <AddressLink
              sx={{
                "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
              }}
              type="trust"
              address={transaction.to as string}
            />
          </ClickableContainer>
        );

      default:
        if (transaction.to) {
          return (
            <ClickableContainer>
              <AddressLink
                sx={{
                  "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                  "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                }}
                type="user"
                address={transaction.to}
              />
            </ClickableContainer>
          );
        }

        if (transaction.data && transaction.data.hasOwnProperty("isPatron")) {
          return (
            <ClickableContainer>
              <AddressLink
                sx={{
                  "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                  "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                }}
                address={transaction.data.isPatron ? transaction.data.patron : transaction.data.guardian}
                type="user"
              />
            </ClickableContainer>
          );
        }

        return (
          <ClickableContainer>
            <Text numeric contrast medium>
              -
            </Text>
          </ClickableContainer>
        );
    }
  },
};

// The transaction Name column is 220px wide (pinned on the header); the label is clamped to that
// minus the cell's right padding (18px / 24px) so a long land or member name ellipsises on one
// line rather than wrapping onto a second row. See NAME_TEXT_WIDTH for why this must be px.
const TXN_NAME_TEXT_WIDTH = { xs: "202px", md: "196px" };

/** The one-line, ellipsising label shared by every branch of the transaction Name column. */
const TransactionName = ({ name }: { name?: string | null }) => (
  <TruncatedText title={name || ""} sx={{ maxWidth: TXN_NAME_TEXT_WIDTH }}>
    <Text
      numeric
      contrast
      capital
      sx={{
        fontSize: { xs: 14, md: 16 },
        textDecoration: "none",
        lineHeight: 1.2,
        color: "black",
        "a:hover &": { color: "black" },
      }}
    >
      {name}
    </Text>
  </TruncatedText>
);

export const transactionLandNameCol: TableSchema<EtTransaction> = {
  id: "name",
  label: "Name",
  sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
  headerStyle: { width: 220, maxWidth: 220, minWidth: 220 },
  render: (txn) => {
    const isReassignLand = Boolean(txn?.type === "reassign_land");
    const userImage =
      txn?.type === EtTransactionType?.TRUST_DISMISSED ||
      txn?.type === EtTransactionType?.TRUST_FLAGGED_FOR_DISMISSAL ||
      txn?.type === EtTransactionType?.TRUST_ROLE_GRANTED ||
      txn?.type === EtTransactionType?.GLOBAL_ADMIN_ADDED ||
      txn?.type === EtTransactionType?.TRUST_ADMIN_ADDED;
    switch (true) {
      case isReassignLand:
        return (
          <Link
            style={{ textDecoration: "none" }}
            to={landDetailLink(txn.etNft?.ecoToken?.id || "")}
            sx={{
              cursor: txn?.nftId ? "pointer" : "not-allowed",
              pointerEvents: txn?.nftId ? "auto" : "none",
            }}
          >
            <ClickableContainer sx={{ flexDirection: "column", alignItems: "flex-start", maxWidth: "100%" }}>
              <TransactionName name={txn?.data?.landName} />
              <Text numeric sx={{ color: "text.disabled", lineHeight: 1.2 }}>
                {txn?.nftId ? `#${txn.nftId}` : null}
              </Text>
            </ClickableContainer>
          </Link>
        );

      case userImage:
        return (
          <Link
            style={{ textDecoration: "none" }}
            to={generatePath(AppRoutes.UserWallet.path, { address: txn?.to || "" })}
          >
            <ClickableContainer sx={{ maxWidth: "100%" }}>
              <TransactionName name={txn?.toUserDetails?.name} />
            </ClickableContainer>
          </Link>
        );

      default:
        return (
          <Link style={{ textDecoration: "none" }} to={landDetailLink(txn.etNft?.ecoToken?.id || "")}>
            <ClickableContainer sx={{ flexDirection: "column", alignItems: "flex-start", maxWidth: "100%" }}>
              <TransactionName name={txn.etNft?.ecoToken?.meta?.name} />
              <Text numeric sx={{ color: "text.disabled", lineHeight: 1.2 }}>
                #{txn.nftId}
              </Text>
            </ClickableContainer>
          </Link>
        );
    }
  },
};

export const ProvenanceMobileSchema: TableSchema<EtTransaction>[] = [
  // transactionThumbCol,
  // hashCol,
  // transactionTypeCol,
  // transactionValueCol,
  transactionTypeCol,
  transactionFromCol,
  transactionToCol,
  transactionValueCol,
  timeCol,
];

export const TransactionsSchema: TableSchema<EtTransaction>[] = [
  transactionThumbCol,
  transactionLandNameCol,
  // hashCol,
  transactionTypeCol,
  transactionFromCol,
  transactionToCol,
  transactionValueCol,
  timeCol,
];
export const BidSchema: TableSchema<EtTransaction>[] = [
  transactionTypeCol,
  transactionFromCol,
  transactionValueCol,
  timeCol,
];

export const ProvenanceSchema: TableSchema<EtTransaction>[] = [
  // transactionThumbCol,
  // transactionLandNameCol,

  transactionTypeCol,
  transactionFromCol,
  transactionToCol,
  transactionValueCol,
  timeCol,
];

export const TransactionMobileSchema: TableSchema<EtTransaction>[] = [
  transactionThumbCol,
  // hashCol,
  transactionTypeCol,
  transactionValueCol,
  timeCol,
];

export const EcoBankLandsSchema = [
  ...baseLandsSchema,
  ageCol,
  scoreCol,
  // ownerCol,
  // trustCol,
  // publicStatusCol,
  // treeMintRateCol,
  //remainingMintCol,
  remainingMintBarCol,
  totalMintCol,

  auctionStatusCol,

  // easementActivatedCol,
  valueCol,
];

export const TrusteeLandsSchema = [
  ...baseLandsSchema,
  scoreCol,
  ageCol,
  remainingMintBarCol,
  //ownerCol,
  // guardianCol,
  // publicStatusCol,
  totalMintCol,
  // treeMintRateCol,
  // auctionStatusCol,
  // easementActivatedCol,

  auctionStatusCol,
  valueCol,
];

export const EcoBankAssetsSchema = [
  ...baseLandsSchema,
  ageCol,
  scoreCol,
  remainingMintBarCol,
  totalMintCol,
  auctionStatusCol,
  valueCol,
];

// Mobile-friendly Tree Supply Details Component
type TreeSupplyDetailsProps = {
  rate: number;
  treeSupplyData: {
    mintCapacity: string;
    claimedMint: string;
    remainingMint: string;
    activeTime: React.ReactNode | string;
    patronRedeemedReward: string;
    guardianRedeemedReward: string;
    referralRedeemedReward: string;
  };
};

const TreeSupplyDetails = ({ rate, treeSupplyData }: TreeSupplyDetailsProps) => {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleTooltipToggle = () => setTooltipOpen(!tooltipOpen);
  const handleTooltipClose = () => setTooltipOpen(false);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) && tooltipOpen) {
        setTooltipOpen(false);
      }
    };

    if (tooltipOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tooltipOpen]);

  const rateDisplay = (
    <Text numeric contrast medium>
      {sixDigits(formatValue(rate, true, 3))} <CurrencyLabel />
    </Text>
  );

  if (isMobile) {
    return (
      <>
        <ClickableContainer onClick={handleOpen}>
          {rateDisplay}
          {/* <InfoIcon style={{ width: "15px", height: "15px", }} /> */}
        </ClickableContainer>

        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          disableSwipeToOpen={false}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              maxHeight: "70vh",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "auto",
              height: "auto",
              flexDirection: "column",
              alignItems: "start",
              backgroundColor: "white",
              padding: "22px 25px",
              gap: 2,
            }}
          >
            {/* Title Header with Close Icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                marginBottom: "16px",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#000",
                }}
              >
                Mint Currency
              </Typography>
              <IconButton
                onClick={handleClose}
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: "20px", color: "#666" }} />
              </IconButton>
            </Box>
            <Grid
              container
              gap={"40px"}
              sx={{ width: "100%", flexDirection: "row", justifyContent: "space-between", marginBottom: "20px" }}
            >
              <Grid
                item
                xs={"auto"}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: "112px",
                  justifyContent: "end",
                }}
              >
                <PopUpText>
                  Remaining Mint:{" "}
                  <span
                    style={{ display: "flex", alignItems: "center", textDecoration: "none", justifyContent: "end" }}
                  >
                    {treeSupplyData.remainingMint}{" "}
                    <CurrencyLabel
                      currency={TREE_CURRENCY}
                      sx={{ textDecoration: "none !important", fontSize: "14px" }}
                    />
                  </span>
                </PopUpText>
              </Grid>
              <Grid
                item
                xs={"auto"}
                sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "112px" }}
              >
                <PopUpText sx={{ textAlign: "left" }}>
                  Total Mint :{" "}
                  <PopUpText>
                    <span style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                      {treeSupplyData.mintCapacity}{" "}
                      <CurrencyLabel
                        currency={TREE_CURRENCY}
                        sx={{ textDecoration: "none !important", fontSize: "14px" }}
                      />
                    </span>
                  </PopUpText>
                </PopUpText>
              </Grid>
            </Grid>
            <Divider sx={{ marginBottom: "15px", width: "100%", backgroundColor: "#E0E0E0" }} />
            <Grid
              container
              gap={"8px"}
              sx={{ width: "100%", flexDirection: "column", alignContent: "end", marginBottom: "20px" }}
            >
              <Grid
                item
                xs={"auto"}
                sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
              >
                <PopUpText>
                  Patron: {treeSupplyData.patronRedeemedReward}{" "}
                  <CurrencyLabel
                    currency={TREE_CURRENCY}
                    sx={{ textDecoration: "none !important", fontSize: "14px" }}
                  />
                </PopUpText>
              </Grid>
              <Grid
                item
                xs={"auto"}
                sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
              >
                <PopUpText>
                  Guardian: {treeSupplyData.guardianRedeemedReward}{" "}
                  <CurrencyLabel
                    currency={TREE_CURRENCY}
                    sx={{ textDecoration: "none !important", fontSize: "14px" }}
                  />
                </PopUpText>
              </Grid>
              <Grid
                item
                xs={"auto"}
                sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
              >
                <PopUpText>
                  Referer:{treeSupplyData.referralRedeemedReward}{" "}
                  <CurrencyLabel
                    currency={TREE_CURRENCY}
                    sx={{ textDecoration: "none !important", fontSize: "14px" }}
                  />
                </PopUpText>
              </Grid>
            </Grid>
            <Divider sx={{ marginBottom: "15px", borderStyle: "dashed", width: "100%", backgroundColor: "#E0E0E0" }} />{" "}
            <Grid container sx={{ width: "100%", flexDirection: "row", justifyContent: "end" }}>
              <Grid
                item
                xs={"auto"}
                sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
              >
                <PopUpText sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                  {treeSupplyData.activeTime && typeof treeSupplyData.activeTime === "object"
                    ? "ACTIVE TIME"
                    : "CONTRACT PERIOD"}{" "}
                  : {treeSupplyData.activeTime}
                </PopUpText>
              </Grid>
            </Grid>
          </Box>
        </SwipeableDrawer>
      </>
    );
  }

  // Desktop tooltip (now click-based, making it more like a popover)
  return (
    <div ref={tooltipRef}>
      <ClickableContainer onClick={tooltipOpen ? handleTooltipClose : handleTooltipToggle}>
        {rateDisplay}
        {/* <InfoIcon style={{ width: "15px", height: "15px", }} /> */}
      </ClickableContainer>
      <Popover
        classes={{ paper: "PopoverPaper" }}
        open={tooltipOpen}
        anchorEl={tooltipOpen ? tooltipRef.current : null}
        onClose={handleTooltipClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {" "}
        <Box
          sx={{
            display: "flex",
            width: "auto",
            height: "auto",
            flexDirection: "column",
            alignItems: "start",
            backgroundColor: "white",
            padding: "12px",
            gap: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Text bold medium>
              Mint Currency
            </Text>
            <IconButton size="small" onClick={handleTooltipClose}>
              <Close />
            </IconButton>
          </div>

          {/* <Typography variant="body2">
                        ${formatValue(remainingInEasement, true, 2)} @ ${formatValue(mintRate, true, 4)}
                      </Typography>
                      {token.etNft?.status !== "sold" && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {getMintRate()} TREE Per day
                        </Typography>
                      )} */}
          {/* <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                        {getRemainingTrees()}  Mint Capacity
                        {getMintRate()} TREE Per day
                      </Typography> */}
          <Grid
            container
            gap={"40px"}
            sx={{ width: "100%", flexDirection: "row", justifyContent: "space-between", marginBottom: "20px" }}
          >
            <Grid
              item
              xs={"auto"}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "112px",
                justifyContent: "end",
              }}
            >
              <PopUpText>
                Remaining Mint:{" "}
                <span style={{ display: "flex", alignItems: "center", textDecoration: "none", justifyContent: "end" }}>
                  {treeSupplyData.remainingMint}{" "}
                  <CurrencyLabel
                    currency={TREE_CURRENCY}
                    sx={{ textDecoration: "none !important", fontSize: "14px" }}
                  />
                </span>
              </PopUpText>
            </Grid>
            <Grid
              item
              xs={"auto"}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "112px" }}
            >
              <PopUpText sx={{ textAlign: "left" }}>
                Total Mint :{" "}
                <PopUpText>
                  <span style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                    {treeSupplyData.mintCapacity}{" "}
                    <CurrencyLabel
                      currency={TREE_CURRENCY}
                      sx={{ textDecoration: "none !important", fontSize: "14px" }}
                    />
                  </span>
                </PopUpText>
              </PopUpText>
            </Grid>
          </Grid>
          <Divider sx={{ marginBottom: "15px", width: "100%", backgroundColor: "#E0E0E0" }} />
          <Grid
            container
            gap={"8px"}
            sx={{ width: "100%", flexDirection: "column", alignContent: "end", marginBottom: "20px" }}
          >
            <Grid
              item
              xs={"auto"}
              sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
            >
              <PopUpText>
                Patron: {treeSupplyData.patronRedeemedReward}{" "}
                <CurrencyLabel currency={TREE_CURRENCY} sx={{ textDecoration: "none !important", fontSize: "14px" }} />{" "}
              </PopUpText>
            </Grid>
            <Grid
              item
              xs={"auto"}
              sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
            >
              <PopUpText>
                Guardian: {treeSupplyData.guardianRedeemedReward}{" "}
                <CurrencyLabel currency={TREE_CURRENCY} sx={{ textDecoration: "none !important", fontSize: "14px" }} />{" "}
              </PopUpText>
            </Grid>

            <Grid
              item
              xs={"auto"}
              sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
            >
              <PopUpText>
                Referer: {treeSupplyData.referralRedeemedReward}{" "}
                <CurrencyLabel currency={TREE_CURRENCY} sx={{ textDecoration: "none !important", fontSize: "14px" }} />{" "}
              </PopUpText>
            </Grid>
          </Grid>
          <Divider sx={{ marginBottom: "15px", borderStyle: "dashed", width: "100%", backgroundColor: "#E0E0E0" }} />
          <Grid container sx={{ width: "100%", flexDirection: "row", justifyContent: "end" }}>
            <Grid
              item
              xs={"auto"}
              sx={{ display: "flex", flexDirection: "column", alignItems: "end", minWidth: "112px" }}
            >
              <PopUpText sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
                {treeSupplyData.activeTime && typeof treeSupplyData.activeTime === "object"
                  ? "ACTIVE TIME"
                  : "CONTRACT PERIOD"}{" "}
                : {treeSupplyData.activeTime}
              </PopUpText>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </div>
  );
};

// Styled Components
export const ClickableContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "start",
  justifyContent: "start",
  gap: "4px",
  cursor: "pointer",
  borderRadius: "8px",
  width: "fit-content",
  padding: "4px 8px",
  margin: "-4px -8px",
  transition: "background-color 0.4s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "&:active": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
}));

export const MobileEcoBankLandsSchema = [
  imgCol,
  nameCol,
  locationCol,
  // publicStatusCol,
  scoreCol,
  // treeMintRateCol,
  // remainingMintCol,
  remainingMintBarCol,
  totalMintCol,

  auctionStatusCol,

  txnLatestLandCol,

  ageColLatestLands,
];
